import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router";
import type { DashboardDetail, DashboardSummary } from "@astrum/shared";
import { Button, EmptyState, Icon, Input, Modal, PageBody, Pill, SkeletonText, useToast } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./dashboards.css";

const PAGE = 25;

export function Dashboards() {
  usePageTitle("Dashboards");
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rows, setRows] = useState<DashboardSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async (after: string | null) => {
      try {
        const res = await api.get<{ dashboards: DashboardSummary[]; nextCursor: string | null }>(
          `/dashboards?limit=${PAGE}${after ? `&cursor=${after}` : ""}`,
        );
        setRows((prev) => (after ? [...prev, ...res.dashboards] : res.dashboards));
        setCursor(res.nextCursor);
        setFailed(false);
      } catch (error) {
        setFailed(true);
        toast(error instanceof ApiError ? error.message : "Could not load dashboards.", { tone: "danger" });
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      const res = await api.post<{ dashboard: DashboardDetail }>("/dashboards", { name: name.trim() });
      navigate(`/dashboards/${res.dashboard.id}`);
    } catch (error) {
      toast(error instanceof ApiError ? error.message : "Could not create the dashboard.", { tone: "danger" });
      setBusy(false);
    }
  };

  const mine = rows.filter((r) => r.mine);
  const shared = rows.filter((r) => !r.mine);

  const renderRow = (d: DashboardSummary) => (
    <button key={d.id} type="button" className="dash-list__row" onClick={() => navigate(`/dashboards/${d.id}`)}>
      <Icon name="insights" size={18} />
      <span className="dash-list__main">
        <span className="dash-list__name">{d.name}</span>
        <span className="dash-list__meta">
          {d.widgetCount} {d.widgetCount === 1 ? "widget" : "widgets"}
          {!d.mine && d.ownerName ? ` · by ${d.ownerName}` : ""}
        </span>
      </span>
      {d.shared && <Pill tone="neutral">Shared</Pill>}
    </button>
  );

  return (
    <PageBody
      title="Dashboards"
      subtitle="Charts and numbers for your work, your team or the whole office."
      width="regular"
      actions={
        <Button size="sm" icon={<Icon name="plus" size={13} />} onClick={() => setCreating(true)}>
          New dashboard
        </Button>
      }
    >
      {loading ? (
        <SkeletonText lines={4} />
      ) : failed && rows.length === 0 ? (
        <EmptyState icon="insights" title="Dashboards could not be loaded" description="Check your connection and try again." />
      ) : rows.length === 0 ? (
        <EmptyState icon="insights" title="No dashboards yet" description="Create one and add the widgets you want to watch." />
      ) : (
        <>
          <h2 className="dash-widget__title">Yours</h2>
          <div className="dash-list">{mine.map(renderRow)}</div>
          {shared.length > 0 && (
            <>
              <h2 className="dash-widget__title" style={{ marginTop: "var(--sp-6)" }}>
                Shared with you
              </h2>
              <div className="dash-list">{shared.map(renderRow)}</div>
            </>
          )}
          {cursor && (
            <Button variant="secondary" size="sm" onClick={() => void load(cursor)}>
              Show more
            </Button>
          )}
        </>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="New dashboard">
        <form className="dash-form" onSubmit={(e) => void create(e)}>
          <Input label="Name" value={name} maxLength={100} autoFocus onChange={(e) => setName(e.target.value)} />
          <div className="dash-form__actions">
            <Button variant="ghost" type="button" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} disabled={!name.trim()}>
              Create
            </Button>
          </div>
        </form>
      </Modal>
    </PageBody>
  );
}
